/**
 * Click / tap / hold / drag / pinch on the game canvas (docs/design/combat.md §Control
 * model, "Explicit target" and "Touch camera"; AD-817).
 *
 * One Pointer Events path serves mouse, pen and touch:
 *
 * - **Press on a hostile** (picked in screen space, `combat/hostilePick.ts`):
 *   released before `HOLD_PRESS_MS` without moving more than `TAP_SLOP_PX` ⇒ `attack`
 *   (explicit target + basic attack, walk-to if out of reach); still down at
 *   `HOLD_PRESS_MS` ⇒ `hold` (the `combat/holdAction.ts` seam — today weapon ability 1).
 * - **Press on the ground:** a MOUSE moves at once, exactly as before this story (desktop
 *   click-to-move is unchanged); a FINGER or PEN moves on release (or at the hold
 *   threshold), because until then it may still turn into a camera drag.
 * - **One-finger (or pen) drag** beyond `TAP_SLOP_PX` ⇒ `orbit` (camera yaw/pitch), and
 *   the press is no longer a tap. A mouse drag only cancels the click — the mouse
 *   orbits with the right button, which `OrbitCamera` keeps owning.
 * - **Two fingers** ⇒ `zoom` by the change in their span; the press is cancelled; the
 *   finger left after a pinch keeps orbiting and never taps.
 *
 * `TapControls` is pure (no DOM, no THREE): raw pointer samples in, sink calls out,
 * so every classification is unit-tested. `attachTapControls` is the thin DOM binding.
 */

import { type PointerKind } from './combat/hostilePick.js';
import { runTeardown } from './teardown.js';

/**
 * Movement, in CSS px, past which a press is a drag, not a tap. 10 px is above finger
 * jitter on a deliberate tap (a few px) and well under any intended camera drag.
 */
export const TAP_SLOP_PX = 10;

/**
 * How long a press on a hostile is held before it is a HOLD, ms. 350 ms: a deliberate
 * tap or click is ~80–200 ms down, so a tap is never misread; it stays under the OS
 * long-press (~500 ms on iOS/Android, which would raise selection or a context menu
 * first); and it is short enough that the hold still feels like one action.
 */
export const HOLD_PRESS_MS = 350;

export interface RawPointer {
  id: number;
  /** Canvas-relative CSS px. */
  x: number;
  y: number;
  kind: PointerKind;
  /** `PointerEvent.button` (0 = primary; touch and pen contact report 0). */
  button: number;
}

export interface TapControlsSink {
  /** The hostile under canvas px (x, y) for this pointer kind, or null. */
  pickHostile(x: number, y: number, kind: PointerKind): number | null;
  /** A ground click/tap at canvas px (x, y): move there. */
  moveTo(x: number, y: number): void;
  /** A click/tap on hostile `id`: make it the explicit target and basic-attack it. */
  attack(id: number): void;
  /** A held press on hostile `id`: make it the explicit target and run the hold action. */
  hold(id: number): void;
  /** Camera orbit by a drag delta, CSS px. */
  orbit(dx: number, dy: number): void;
  /** Camera zoom: multiply the boom distance by `ratio` (> 1 = out). */
  zoom(ratio: number): void;
}

type PressState = 'pending' | 'dragging' | 'held' | 'done';

interface Press {
  id: number;
  kind: PointerKind;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  t0: number;
  hostile: number | null;
  state: PressState;
}

export class TapControls {
  private readonly pointers = new Map<number, { x: number; y: number; kind: PointerKind }>();
  private press: Press | null = null;
  private pinchSpan: number | null = null;

  constructor(private readonly sink: TapControlsSink) {}

  /** Whether a press is pending a tap/hold decision (diagnostics + tests). */
  get pendingPress(): boolean {
    return this.press?.state === 'pending';
  }

  /** Whether two fingers are pinching. */
  get pinching(): boolean {
    return this.pinchSpan !== null;
  }

  down(p: RawPointer, now: number): void {
    if (p.kind === 'mouse' && p.button !== 0) return; // right-drag orbit stays OrbitCamera's
    if (this.pointers.size >= 2) return; // a third finger is ignored
    this.pointers.set(p.id, { x: p.x, y: p.y, kind: p.kind });
    if (this.pointers.size === 2 && p.kind !== 'mouse') {
      this.press = null; // a second finger cancels the tap/hold
      this.pinchSpan = this.span();
      return;
    }
    if (this.pointers.size !== 1) return;
    const hostile = this.sink.pickHostile(p.x, p.y, p.kind);
    this.press = {
      id: p.id,
      kind: p.kind,
      startX: p.x,
      startY: p.y,
      lastX: p.x,
      lastY: p.y,
      t0: now,
      hostile,
      state: 'pending',
    };
    if (hostile === null && p.kind === 'mouse') {
      // Desktop click-to-move is unchanged: the mouse moves on press.
      this.sink.moveTo(p.x, p.y);
      this.press.state = 'done';
    }
  }

  move(p: RawPointer): void {
    const known = this.pointers.get(p.id);
    if (known === undefined) return;
    known.x = p.x;
    known.y = p.y;
    if (this.pinchSpan !== null) {
      const span = this.span();
      if (span > 0 && this.pinchSpan > 0) this.sink.zoom(this.pinchSpan / span);
      this.pinchSpan = span;
      return;
    }
    const press = this.press;
    if (press === null || press.id !== p.id) return;
    if (press.state === 'dragging') {
      this.sink.orbit(p.x - press.lastX, p.y - press.lastY);
    } else if (Math.hypot(p.x - press.startX, p.y - press.startY) > TAP_SLOP_PX) {
      if (press.kind !== 'mouse') {
        press.state = 'dragging';
        this.sink.orbit(p.x - press.lastX, p.y - press.lastY);
      } else if (press.state === 'pending') {
        press.state = 'done'; // a mouse drag off a hostile is not a click
      }
    }
    press.lastX = p.x;
    press.lastY = p.y;
  }

  up(p: RawPointer): void {
    if (!this.pointers.delete(p.id)) return;
    if (this.pinchSpan !== null) {
      this.endPinchIfNeeded();
      return;
    }
    const press = this.press;
    if (press === null || press.id !== p.id) return;
    this.press = null;
    if (press.state !== 'pending') return;
    if (press.hostile !== null) this.sink.attack(press.hostile);
    else this.sink.moveTo(press.startX, press.startY);
  }

  /** `pointercancel` / lost capture: forget the pointer, never act on it. */
  cancel(id: number): void {
    if (!this.pointers.delete(id)) return;
    if (this.pinchSpan !== null) {
      this.endPinchIfNeeded();
      return;
    }
    if (this.press?.id === id) this.press = null;
  }

  /** Per frame: a pending press reaching `HOLD_PRESS_MS` becomes a hold. */
  tick(now: number): void {
    const press = this.press;
    if (press === null || press.state !== 'pending' || now - press.t0 < HOLD_PRESS_MS) return;
    press.state = 'held';
    if (press.hostile !== null) this.sink.hold(press.hostile);
    // A finger resting on the ground is a slow tap: move now (a mouse already has).
    else this.sink.moveTo(press.startX, press.startY);
  }

  private span(): number {
    const [a, b] = [...this.pointers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }

  private endPinchIfNeeded(): void {
    if (this.pointers.size >= 2) return;
    this.pinchSpan = null;
    const rest = [...this.pointers.entries()][0];
    // The finger left on the glass keeps turning the camera; it never taps.
    this.press =
      rest === undefined
        ? null
        : {
            id: rest[0],
            kind: rest[1].kind,
            startX: rest[1].x,
            startY: rest[1].y,
            lastX: rest[1].x,
            lastY: rest[1].y,
            t0: 0,
            hostile: null,
            state: 'dragging',
          };
  }
}

function pointerKind(e: PointerEvent): PointerKind {
  return e.pointerType === 'touch' || e.pointerType === 'pen' ? e.pointerType : 'mouse';
}

/**
 * Bind `controls` to a canvas: pointer down/move/up/cancel, canvas-relative px,
 * pointer capture for touch/pen, `touch-action: none` so the browser neither scrolls
 * nor zooms the page under a game gesture. `onHover` (mouse only, no button down)
 * lets the caller show a hostile cursor. Returns the detach.
 */
export function attachTapControls(
  el: HTMLElement,
  controls: TapControls,
  now: () => number,
  onHover?: (x: number, y: number) => void,
): () => void {
  const at = (e: PointerEvent): RawPointer => {
    const rect = el.getBoundingClientRect();
    return {
      id: e.pointerId,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      kind: pointerKind(e),
      button: e.button,
    };
  };
  const onDown = (e: PointerEvent): void => {
    const p = at(e);
    if (p.kind !== 'mouse') {
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // A pointer that already ended cannot be captured; the press still classifies.
      }
    }
    controls.down(p, now());
  };
  const onMove = (e: PointerEvent): void => {
    const p = at(e);
    controls.move(p);
    if (p.kind === 'mouse' && e.buttons === 0) onHover?.(p.x, p.y);
  };
  const onUp = (e: PointerEvent): void => controls.up(at(e));
  const onCancel = (e: PointerEvent): void => controls.cancel(e.pointerId);
  const previousTouchAction = el.style.touchAction;
  el.style.touchAction = 'none';
  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onCancel);
  return () =>
    runTeardown([
      () => el.removeEventListener('pointerdown', onDown),
      () => el.removeEventListener('pointermove', onMove),
      () => el.removeEventListener('pointerup', onUp),
      () => el.removeEventListener('pointercancel', onCancel),
      () => {
        el.style.touchAction = previousTouchAction;
      },
    ]);
}
